// Safe translation string accessor
const getTranslation = (path, fallback = '') => {
  try {
    const keys = path.split('.');
    let value = window.themeVariables?.strings;

    for (const key of keys) {
      if (value === undefined || value === null) {
        console.warn(`Translation missing: ${path}`);
        return fallback;
      }
      value = value[key];
    }

    return value !== undefined && value !== null ? value : fallback;
  } catch (error) {
    console.warn(`Translation error for ${path}:`, error);
    return fallback;
  }
};

// Claim/Return lookup form validation web component
class ClaimReturnLookupForm extends HTMLElement {
  constructor() {
    super();
    this.form = null;
    this.feedback = null;
  }

  connectedCallback() {
    this.form = this.querySelector('[data-claim-return-lookup-form]');
    if (!this.form) return;

    this.feedback = this.form.querySelector('[data-feedback]');
    this.form.addEventListener('submit', this.handleSubmit.bind(this));
  }

  disconnectedCallback() {
    if (this.form) {
      this.form.removeEventListener('submit', this.handleSubmit.bind(this));
    }
  }

  buildDestinationUrl() {
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return `${root}pages/create-claim-return`;
  }

  async requestLookup(identifier, customerEmail) {
    const params = new URLSearchParams({
      customer_email: customerEmail,
      shopify_order_name: identifier
    });
    const res = await fetch(`https://rice-service.production.dtails.app/frontend/invoices?${params.toString()}`, {
      method: 'GET',
      headers: { 'Accept': 'application/json' }
    });
    if (!res.ok) {
      let message = getTranslation('claims_returns_form.errors.general', 'An error occurred');
      if (res.status === 404) message = getTranslation('claims_returns_form.errors.order_not_found', 'Order not found');
      else if (res.status === 403) message = getTranslation('claims_returns_form.errors.incorrect_input', 'Incorrect input');
      else if (res.status === 400) message = getTranslation('claims_returns_form.errors.input_missing', 'Input missing');
      throw message;
    }
    return res.json();
  }

  async handleSubmit(ev) {
    ev.preventDefault();
    const data = Object.fromEntries(new FormData(this.form));
    if (this.feedback) this.feedback.style.display = 'none';

    try {
      await this.requestLookup(data.identifier, data.customer_email);
      const qs = new URLSearchParams(data).toString();
      window.location.href = `${this.buildDestinationUrl()}?${qs}`;
    } catch (err) {
      if (this.feedback) {
        this.feedback.innerHTML = `${err}`;
        this.feedback.style.display = 'block';
      }
    }
  }
}

customElements.define('claim-return-lookup', ClaimReturnLookupForm);

// Claims/Returns page web component
class ClaimsReturnsPage extends HTMLElement {
  constructor() {
    super();

    const qs = new URLSearchParams(window.location.search);
    this.identifier = qs.get("identifier") || "";
    this.customerEmail = qs.get("customer_email") || "";
    this.type = (qs.get("type") || "").toLowerCase(); // 'claim' | 'return'

    this.state = {
      order: null,
      loading: true,
      error: "",
      submitting: false,
      submitted: false
    };
  }

  connectedCallback() {
    if (!this.identifier || !this.customerEmail || (this.type !== "claim" && this.type !== "return")) {
      return this.goHome();
    }

    this.causes = this.type === "claim"
      ? [
          { key: 1, id: "BROKENTR", label: getTranslation('claims_page.reason_1', 'Product broken during transport'), imageRequired: true },
          { key: 2, id: "MISARTSTOC", label: getTranslation('claims_page.reason_2', 'Wrong product received'), imageRequired: false },
          { key: 3, id: "MISARTSTOC", label: getTranslation('claims_page.reason_3', 'Missing product'), imageRequired: false },
          { key: 4, id: "MISARTSTOC", label: getTranslation('claims_page.reason_4', 'Defective product'), imageRequired: true },
          { key: 5, id: "NCSUP", label: getTranslation('claims_page.reason_5', 'Quality issue'), imageRequired: true }
        ]
      : [
          { key: 6, id: "NECUSTOMER", label: getTranslation('returns_page.reason_1', 'Changed mind'), imageRequired: false },
          { key: 7, id: "NECUSTOMER", label: getTranslation('returns_page.reason_2', 'No longer needed'), imageRequired: false }
        ];

    this.render();
    this.fetchOrder();
  }

  goHome() {
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || "/";
    window.location.href = root;
  }

  formatDate(str) {
    try { return new Date(str).toLocaleDateString(); } catch { return str || ""; }
  }

  setHTML(html) {
    this.innerHTML = html;
  }

  encodeFileToBase64(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }

  render() {
    if (this.state.loading) {
      return this.setHTML(`<p></p>`);
    }
    if (this.state.fetchError) {
      return this.setHTML(`<div class="note errors" role="alert">${this.state.fetchError}</div>`);
    }
    if (this.state.submitted) {
      const successMsg = this.type === "claim"
        ? getTranslation('claims_page.success_message', 'Your claim has been submitted successfully')
        : getTranslation('returns_page.success_message', 'Your return has been submitted successfully');
      return this.setHTML(`<div class="note note--success" role="status">${successMsg}</div>`);
    }

    const order = this.state.order;
    const title = this.type === "claim"
      ? getTranslation('claims_page.title', 'Submit a Claim')
      : getTranslation('returns_page.title', 'Submit a Return');

    const headerHtml = `
      <header class="section-header section-header--with-link">
        <h1 class="section-header__title">${title}</h1>
      </header>
      <div class="grid">
        <div class="grid__item">
          <table class="table--responsive table--small-text table-claims">
            <thead><tr>
              <th>${order.entity === "invoice" ? "Document No" : getTranslation('claims_returns_text.order', 'Order')}</th>
              <th>${getTranslation('claims_returns_text.date', 'Date')}</th>
              <th>${getTranslation('claims_returns_text.total', 'Total')}</th>
            </tr></thead>
            <tbody>
              <tr>
                <td data-label="Order No">${order.orderDisplayName || ""}</td>
                <td data-label="Date">${this.formatDate(order.createdAt)}</td>
                <td data-label="Total">${order.total} ${order.currencyCode || ""}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    `;

    const reasonPlaceholder = this.type === "claim"
      ? getTranslation('claims_page.reason_placeholder', 'Select reason')
      : getTranslation('returns_page.reason_placeholder', 'Select reason');

    const quantityLabel = this.type === "claim"
      ? getTranslation('claims_page.quantity', 'Quantity')
      : getTranslation('returns_page.quantity', 'Quantity');

    const describeLabel = this.type === "claim"
      ? getTranslation('claims_page.describe', 'Describe the issue')
      : getTranslation('returns_page.describe', 'Additional information');

    const linesHtml = (order.lines || []).map((line, idx) => {
      const selected = line.__selected || false;
      const rowControls = !selected ? "" : `
        <tr>
          <td colspan="6" class="table-claims-submit">
            <table>
              <tr>
                <td>
                  <div class="form-group">
                    <label>${reasonPlaceholder}</label>
                    <select data-reason-index="${idx}">
                      <option value="">${reasonPlaceholder}</option>
                      ${this.causes.map(c => `<option value="${c.key}" ${line.__cause === c.key ? "selected" : ""}>${c.label}</option>`).join("")}
                    </select>
                  </div>
                  <div class="form-group">
                    <label>${quantityLabel}</label>
                    <input type="number" min="1" max="${line.quantity}" value="${line.__returnQty || 1}" data-qty-index="${idx}" />
                  </div>
                  <div class="form-group">
                    <label>${describeLabel}</label>
                    <textarea rows="3" data-msg-index="${idx}">${line.__message || ""}</textarea>
                  </div>
                ${this.type === "claim" ? `
                  <div class="form-group">
                    <label>${(() => { const c = this.causes.find(c => c.key === line.__cause); return c && c.imageRequired ? "*" : ""; })()}</label>
                    <input type="file" accept="image/png, image/jpeg" data-file-index="${idx}" />
                  </div>
                ` : ""}
                </td>
              </tr>
            </table>
          </td>
        </tr>
      `;

      const selText = this.type === "claim"
        ? getTranslation('claims_page.select_item', 'Select')
        : getTranslation('returns_page.select_item', 'Select');
      const remove_text = this.type === "claim"
        ? getTranslation('claims_page.remove_item', 'Remove')
        : getTranslation('returns_page.remove_item', 'Remove');

      return `
        <tbody>
          <tr>
            ${order.entity === "invoice" ? `<td data-label="Order No">${line.orderDisplayName || ""}</td>` : ""}
            <td width="120">${line.imageUrl ? `<img src="${line.imageUrl}" alt="" width="80" />` : ""}</td>
            <td data-label="Product">${line.title || line.titleEn || ""}</td>
            <td data-label="SKU">${line.sku || ""}</td>
            <td data-label="Quantity">${line.quantity}</td>
            <td class="text-right">
              ${line.quantity > 0 ? `<button type="button" class="btn btn--small ${selected ? "btn--secondary" : "btn--primary"}" data-select-index="${idx}">${selected ? remove_text : selText}</button>` : ""}
            </td>
          </tr>
          ${rowControls}
        </tbody>
      `;
    }).join("");

    const submitText = this.type === "claim"
      ? getTranslation('claims_page.submit', 'Submit Claim')
      : getTranslation('returns_page.submit', 'Submit Return');
    const order_text = "<th>" + getTranslation('claims_returns_text.order', 'Order') + "</th>";

    const productsHtml = `
      <table class="table--responsive table--small-text table-claims-products">
        <thead>
          <tr>
            ${order.entity === "invoice" ? order_text : ""}
            <th>&nbsp;</th>
            <th>${getTranslation('claims_returns_text.product', 'Product')}</th>
            <th>${getTranslation('claims_returns_text.sku', 'SKU')}</th>
            <th>${getTranslation('claims_returns_text.quantity', 'Quantity')}</th>
            <th>&nbsp;</th>
          </tr>
        </thead>
        ${linesHtml}
      </table>
      <div class="table-claims-button">
        <button type="button" class="btn btn--small" id="claims-submit" ${this.state.submitting ? "disabled" : ""}>${this.state.submitting ? "Submitting…" : submitText}</button>
      </div>
      ${this.state.submissionError ? `<div class="note errors" role="alert">${this.state.submissionError}</div>` : ""}
    `;

    this.setHTML(`${headerHtml}${productsHtml}`);

    // Wire interactions
    this.querySelectorAll('[data-select-index]').forEach(btn => {
      btn.addEventListener('click', () => {
        const i = parseInt(btn.getAttribute('data-select-index'));
        const line = this.state.order.lines[i];
        line.__selected = !line.__selected;
        if (line.__selected) {
          line.__returnQty = line.__returnQty || 1;
          line.__cause = line.__cause || "";
          line.__message = line.__message || "";
          line.__files = line.__files || [];
        }
        this.render();
      });
    });

    this.querySelectorAll('[data-qty-index]').forEach(input => {
      input.addEventListener('input', () => {
        const i = parseInt(input.getAttribute('data-qty-index'));
        const line = this.state.order.lines[i];
        let val = parseInt(input.value || '1');
        if (isNaN(val) || val < 1) val = 1;
        if (val > line.quantity) val = line.quantity;
        line.__returnQty = val;
        input.value = String(val);
      });
    });

    this.querySelectorAll('[data-reason-index]').forEach(sel => {
      sel.addEventListener('change', () => {
        const i = parseInt(sel.getAttribute('data-reason-index'));
        this.state.order.lines[i].__cause = sel.value ? parseInt(sel.value) : "";
        this.render();
      });
    });

    this.querySelectorAll('[data-msg-index]').forEach(txt => {
      txt.addEventListener('input', () => {
        const i = parseInt(txt.getAttribute('data-msg-index'));
        this.state.order.lines[i].__message = txt.value;
      });
    });

    this.querySelectorAll('[data-file-index]').forEach(inp => {
      inp.addEventListener('change', async () => {
        const i = parseInt(inp.getAttribute('data-file-index'));
        const line = this.state.order.lines[i];
        const file = inp.files && inp.files[0];
        if (!file) return;
        if (file.size / 1_000_000 > 5) {
          alert('File size too large. Max 5mb allowed.');
          inp.value = "";
          return;
        }
        const b64 = await this.encodeFileToBase64(file);
        line.__files = [{ name: file.name, data: b64 }];
      });
    });

    const submitBtn = this.querySelector('#claims-submit');
    if (submitBtn) {
      submitBtn.addEventListener('click', async () => {
        if (this.state.submitting || this.state.submitted) return;
        this.state.submissionError = null;
        if (!this.isValid()) {
          this.state.submissionError = 'Please complete required fields for selected items.';
          this.render();
          return;
        }
        this.state.submitting = true;
        this.state.submissionError = null;
        this.render();
        try {
          const productsJson = await window.productLookupBySku(this.state.order.lines.map(l => l.sku))
          const selected = this.state.order.lines.filter(l => l.__selected).map(l => {
            const cause = this.causes.find(c => c.key === l.__cause);
            const base = {
              ...l, // Spread all original line item fields
              quantity: l.__returnQty,
              cause_id: cause?.id,
              cause_translation: cause?.label,
              message: l.__message || ""
            };
            if (this.type === 'claim' && l.__files && l.__files.length) {
              base.files = l.__files;
            }
            if (l.status === 'DRAFT') {
              base.title = l.titleEn
            } else {
              const product = productsJson.find(p => p.variants.some(v => v.sku == l.sku))
              base.title = product?.title || 'N/A'
              base.imageUrl = product?.featured_image
            }
            return base;
          });
          const url = this.type === 'claim' ? 'https://rice-service.production.dtails.app/frontend/claims' : 'https://rice-service.production.dtails.app/frontend/returns';
          const payload = {
            customer: {
              language: (window.Shopify && window.Shopify.locale) || navigator.language || 'en',
              email: this.customerEmail
            },
            lineItems: selected,
            shopifyOrderId: this.state.order.lines[0].shopifyOrderId,
            documentNo: this.state.order.documentNo
          };

          const res = await fetch(url, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
            body: JSON.stringify(payload)
          });
          if (!res.ok) throw new Error('Submission failed');
          this.state.submitted = true;
          this.state.submitting = false;
          this.render();
        } catch (err) {
          this.state.submitting = false;
          this.state.submissionError = (this.type === 'claim' ? 'Claim' : 'Return') + ' submission error.';
          this.render();
        }
      });
    }
  }

  isValid() {
    const selected = (this.state.order.lines || []).filter(l => l.__selected);
    if (selected.length === 0) return false;
    const invalid = selected.filter(l => {
      const cause = this.causes.find(c => c.key === l.__cause);
      const qtyOk = (l.__returnQty || 0) >= 1 && (l.__returnQty || 0) <= (l.quantity || 0);
      const causeOk = !!l.__cause;
      const msgOk = this.type === 'claim' ? (l.__message && l.__message.trim().length > 0) : true;
      const imgOk = this.type === 'claim' && cause && cause.imageRequired ? (l.__files && l.__files.length > 0) : true;
      return !(qtyOk && causeOk && msgOk && imgOk);
    });
    return invalid.length === 0;
  }

  async fetchOrder() {
    try {
      const params = new URLSearchParams({ customer_email: this.customerEmail, shopify_order_name: this.identifier });
      const res = await fetch(`https://rice-service.production.dtails.app/frontend/invoices?${params.toString()}`, { headers: { 'Accept': 'application/json' }});
      if (!res.ok) {
        let msg = getTranslation('claims_returns_form.errors.general', 'An error occurred');
        if (res.status === 404) msg = getTranslation('claims_returns_form.errors.order_not_found', 'Order not found');
        else if (res.status === 403) msg = getTranslation('claims_returns_form.errors.incorrect_input', 'Incorrect input');
        else if (res.status === 400) msg = getTranslation('claims_returns_form.errors.input_missing', 'Input missing');
        throw new Error(msg);
      }
      const data = await res.json();
      // decorate lines for UI
      data.lines = (data.lines || []).map(l => ({
        ...l,
        __selected: false,
        __returnQty: 1,
        __cause: "",
        __message: "",
        __files: []
      }));
      this.state.order = { ...data, entity: 'order' };
      this.state.loading = false;
      this.render();
    } catch (err) {
      this.state.loading = false;
      this.state.fetchError = `${err}`;
      this.render();
    }
  }
}

customElements.define('claims-returns-page', ClaimsReturnsPage);

const productLookupBySku = async (skus) => {
  try {
    const params = new URLSearchParams({
      q: skus.map(sku => `variants.sku:${sku}`).join(' OR '),
      type: 'product',
      view: 'dtails'
    });

    const url = `${window.Shopify.routes.root}search?${params.toString()}`;
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      }
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data || [];
  } catch (e) {
    console.error('Error looking up products by SKU:', e);
    return [];
  }
};

// Make functions globally available
window.productLookupBySku = productLookupBySku;
